import { useEffect, useMemo, useState } from "react";
import { Box } from "@mantine/core";
import { useSelector } from "react-redux";
import { TFunction } from "i18next";

import { ServiceConstants } from "constant";
import { IRootState } from "config/redux";
import { UserService } from "services";
import { AppUtils } from "utils";

import Modal from "./base";
import Button from "../button/standard";
import TextInput from "../input/standard";
import Text from "../text";

const { REQUEST_PENDING, REQUEST_USERS_CLIENTS_UPDATE_PROPERTY_ID } = ServiceConstants;

interface EmailRequiredModalProps {
  opened: boolean;
  userId: number;
  onClose: (val: boolean) => void;
  onSuccess: (email: string) => void;
  t: TFunction;
}

const EmailRequiredModal = ({ opened, userId, onClose, onSuccess, t }: EmailRequiredModalProps) => {
  const requestId = REQUEST_USERS_CLIENTS_UPDATE_PROPERTY_ID + userId + "email";
  const serviceObject = useSelector((state: IRootState) => state.service.object[requestId]);
  const isLoading = useMemo(
    () => serviceObject && serviceObject.status === REQUEST_PENDING,
    [serviceObject],
  );

  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (opened) {
      setValue("");
      setError(null);
    }
  }, [opened]);

  useEffect(() => {
    AppUtils.resolveServiceObject(serviceObject, requestId, () => {
      onSuccess(value);
    });
  }, [serviceObject, requestId, onSuccess, value]);

  const handleSubmit = () => {
    if (!AppUtils.isEmailValid(value)) {
      setError(t("login.error.email"));
      return;
    }
    setError(null);
    UserService.userClientUpdateProperty(userId, "email", value);
  };

  return (
    <Modal opened={opened} onClose={() => onClose(false)} title={t("credit.emailRequired.title")}>
      <Text>{t("credit.emailRequired.description")}</Text>
      <Box mt={20}>
        <TextInput
          label={t("user.email.label")}
          placeholder={t("credit.emailRequired.placeholder")}
          value={value}
          onChange={(event) => setValue(event.currentTarget.value)}
          error={error}
        />
      </Box>
      <Box mt={40}>
        <Button
          fullWidth={true}
          disabled={isLoading}
          loading={isLoading}
          onClick={handleSubmit}
        >
          {t("credit.emailRequired.action")}
        </Button>
      </Box>
    </Modal>
  );
};

export default EmailRequiredModal;
